import AbstractService from 'services/AbstractService';
import NavigationUtils from 'utils/NavigationUtils';

class TabsService extends AbstractService {

    constructor() {
        super();
        this.TAB_CLOSE = 'TAB_CLOSE';
        this.homePath = '/processes';
    }

    getReducer() {
        return (state = this.getInitialState(), action) => {
            let newState = state;
            switch (action.type) {

                case '@@router/LOCATION_CHANGE':
                    newState = this._getLocationChangeState(state, action);
                    localStorage.setItem('openTabs', JSON.stringify(newState.openTabs));
                    break;

                case this.TAB_CLOSE:
                    newState = this._getTabCloseState(state, action);
                    localStorage.setItem('openTabs', JSON.stringify(newState.openTabs));
                    break;

                default:
                    newState = state;
            }
            return newState;
        };
    }


    getInitialState() {
        //Krista didn't like defaulting open tabs to be the same as what you left in previous sessions
        //but wasn't totally sure she wanted that out so just commenting this line out for now
        // const storedOpenTabs = JSON.parse(localStorage.getItem('openTabs')) || ['Home'];
        const storedOpenTabs = ['Home'];

        const subPaths = {};
        for (let i = 0; i < storedOpenTabs.length; i++) {
            if (storedOpenTabs[i] === 'Home') {
                subPaths[storedOpenTabs[i]] = this.homePath;
            } else {
                subPaths[storedOpenTabs[i]] = `/process/${storedOpenTabs[i]}`;
            }
        }

        return {
            openTabs: storedOpenTabs,
            activeTab: 'Home',
            lastTabSubpaths: subPaths // for each tab, the last subpath that was called for that tab
        };
    }

    getStoreKey() {
        return 'tabs';
    }

    /**
     * If the UI is currently not on the base process route, then we can pass it the new route to change to after
     * the tab is renamed.
     * @param oldTabId
     * @param newTabId
     * @param newRoute
     * @param saveAs bool
     */
    renameTab(oldTabId, newTabId, newRoute, saveAs) {
        let tabIds = [];
        // don't rename related tabs if saving a revision as a new process
        if (!saveAs) {
            tabIds = this.getRelatedTabs(oldTabId);
        }

        // sort tabs so that they reopen in same order as they were previously
        // things will still rearrange a bit if multiple processes are open, currently no way to open tabs in specific position
        // but at least things will stay a little more consistent with this
        tabIds.sort((a, b) => {
            const aTab = this.getObjFromTabText(a);
            const bTab = this.getObjFromTabText(b);

            if (aTab.tabPos < bTab.tabPos) {
                return 1;
            } else if (aTab.tabPos > bTab.tabPos) {
                return -1;
            } else {
                return 0;
            }
        });

        // If we changed the process name, we have to close and re-open the tab
        // Note that we can't call TabsService.closeTab() because that does other stuff needed when the user clicks
        // the close button, but we don't want to do that in this case, so we dispatch directly
        this.dispatch({
            type: this.TAB_CLOSE,
            tabIds,
            nextActiveTab: 'Home'
        });
        // Replace the current route with the new tab name
        if (newRoute) {
            NavigationUtils.navigateToPath(newRoute);
        } else {
            tabIds.forEach((tabId) => {
                const tab = this.getObjFromTabText(tabId);
                NavigationUtils.gotoProcess(newTabId, tab.revisionNum);
            });

            // navigate back to the last active tab
            const oldTab = this.getObjFromTabText(oldTabId);
            if (saveAs) {
                NavigationUtils.gotoProcess(newTabId);
            } else {
                NavigationUtils.gotoProcess(newTabId, oldTab.revisionNum);
            }
        }
    }

    getRelatedTabs(tabId) {
        const tabIds = [];
        const tab = this.getObjFromTabText(tabId);
        //need to also rename all the revision tabs that are open
        const myState = this.getMyState(this.getStore().getState());
        myState.openTabs.forEach((openTabId) => {
            const openTab = this.getObjFromTabText(openTabId);
            if (openTab.processId === tab.processId) {
                tabIds.push(openTabId);
            }
        });

        return tabIds;
    }

    getRevisionTabText(processId, revisionNum) {
        return `${processId} (rev ${revisionNum})`;
    }

    getObjFromTabText(tabText) {
        let processId = tabText;
        let revisionNum = null;
        const myState = this.getMyState(this.getStore().getState());
        const tabPos = this._getTabPosition(myState, tabText);

        const pos = tabText.indexOf(' (rev');
        if (pos > -1) {
            processId = tabText.substr(0, pos);
            revisionNum = tabText.replace(`${processId} (rev`, '').replace(')', '').trim();
        }
        return { processId, revisionNum, tabPos };
    }

    closeTab(tabId, isDelete) {
        let tabIds = [];
        const tab = this.getObjFromTabText(tabId);
        if (isDelete || !tab.revisionNum) {
            // if deleting process or closing main process, close all related revision tabs
            tabIds = this.getRelatedTabs(tabId);
        } else {
            tabIds.push(tabId);
        }

        // Asynchronous action, so we can change the route after dispatch
        // if we're closing multiple tabs through a delete just go to home tab
        let nextActiveTab = 'Home';
        if (tabIds.length === 1) {
            nextActiveTab = this._getNextActiveTabAfterClose(tabId);
        }
        const url = this._getLastUrlForTab(nextActiveTab);
        this.dispatch({
            type: this.TAB_CLOSE,
            tabIds,
            nextActiveTab
        });

        if (nextActiveTab === 'Home') {
            NavigationUtils.gotoHome(false);
        } else {
            NavigationUtils.navigateToPath(url);
        }
    }

    _getLastUrlForTab(tabId) {
        const myState = this.getMyState(this.getStore().getState());
        return myState.lastTabSubpaths[tabId] ? myState.lastTabSubpaths[tabId] : this.homePath;
    }

    _getNextActiveTabAfterClose(tabId) {
        const myState = this.getMyState(this.getStore().getState());
        if (tabId !== myState.activeTab) {
            return myState.activeTab;

        } else { // we are closing the active tab\
            const position = this._getTabPosition(myState, tabId);
            return myState.openTabs[position - 1];
        }
    }

    _getTabPosition(state, tabId) {
        let position = -1;
        for (let i = 0; i < state.openTabs.length; i++) {
            const curTabId = state.openTabs[i];
            if (tabId === curTabId) {
                position = i;
                break;
            }
        }
        return position;
    }

    _getTabCloseState(state, action) {
        const newOpenTabs = [...state.openTabs];
        const newLastTabSubpaths = Object.assign({}, state.lastTabSubpaths);

        action.tabIds.forEach((oldTab) => {
            const pos = newOpenTabs.indexOf(oldTab);
            newOpenTabs.splice(pos, 1);

            delete newLastTabSubpaths[oldTab];
        });

        return {
            openTabs: newOpenTabs,
            activeTab: action.nextActiveTab,
            lastTabSubpaths: newLastTabSubpaths
        };
    }

    _getLocationChangeState(state, action) {
        // path is stored in action.payload.pathname
        // (i.e., /process/radflux1longc2/variable-retrieval)

        let newState = state;
        const path = action.payload.location.pathname;

        if (path.startsWith('/process/')) {
            let processId = path.substring(9);
            let revisionNum = null;
            const pos = processId.indexOf('/');
            if (pos >= 0) {
                processId = processId.substr(0, pos);
                if (path.indexOf('/revision/') >= 0) {
                    revisionNum = path.substring((19 + processId.length)); // /process/ (9) + processId (string.length) + /revision/ (10)
                    const revPos = revisionNum.indexOf('/');
                    if (revPos >= 0) {
                        revisionNum = revisionNum.substr(0, revPos);
                    }

                    processId = this.getRevisionTabText(processId, revisionNum);
                }
            }
            const newLastTabSubpaths = Object.assign({}, state.lastTabSubpaths);
            newLastTabSubpaths[processId] = path;

            if (state.openTabs.indexOf(processId) < 0) {
                // zoe don't undo this again (you already broke this once) :( add newly opened process tab at BEGINNING of open tabs in order
                // for us to NOT have to scroll newly opened tab into view when there are more tabs than will fit
                const newOpenTabs = [processId, ...state.openTabs];
                newState = Object.assign({}, state,
                    {
                        activeTab: processId,
                        openTabs: newOpenTabs,
                        lastTabSubpaths: newLastTabSubpaths
                    });
            } else {
                newState = Object.assign({}, state,
                    {
                        activeTab: processId,
                        lastTabSubpaths: newLastTabSubpaths
                    });
            }
        } else if (path === this.homePath) {
            newState = Object.assign({}, state,
                {
                    activeTab: 'Home'
                });
        }

        return newState;
    }

}

export default new TabsService();



// WEBPACK FOOTER //
// ./src/services/TabsService.js